import z from "zod";
import { useQuery } from "react-query";

import { loggedInApiCall } from "src/api/axiosClient/userApi";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { SortType } from "src/api/apiHelpers.ts";
import { isQueryReady } from "src/api/axiosClient/utils";
import { hydraSearchToAppSearch } from "src/api/_common/hydra/search";
import { OlapScoresResult, OlapScoresResultSchema } from "../olapScores/olapScoresSchema";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";

export type TagFilterConfig = {
    mode: "has_type" | "has_value" | "exact_values" | "type_and_value" | "predefined";
    values?: string[];
    type?: string;
    value?: string;
};

type Options = {
    cacheTime?: number;
    page?: number;
    itemsPerPage?: number;
    search?: string;
    productLine?: string;
    productLineNames?: string[];
    campaignId?: number;
    sorting?: Array<SortType>;
    tagFilters?: Record<string, TagFilterConfig>;

    filters: AxiosParams;
    sortingParams: AxiosParams;
    scoresQuery?: OlapScoresQuery;
};

export type Tag = {
    "@id": string;
    id: number;
    name: string;
};

export const productGridKey = "productGrid" as const;

export const useGetAllProduct = (options?: Options) => {
    const {
        cacheTime = 0,
        page = 1,
        itemsPerPage = 20,
        search = "",
        productLine = "",
        productLineNames,
        scoresQuery,
        campaignId,
        sorting = [],
        sortingParams = {},
        filters = {},
        tagFilters = {},
    } = options || {};

    const isEnabled = campaignId !== undefined;

    const effectiveProductLineFilter =
        productLineNames && productLineNames.length > 0 ? productLineNames.join(",") : productLine;

    // Transform tagFilters to API parameters
    const transformTagFiltersToApiParams = (tagFilters: Record<string, TagFilterConfig>): AxiosParams => {
        const tagParams: AxiosParams = {};

        Object.entries(tagFilters).forEach(([tagTypeSlug, config]) => {
            switch (config.mode) {
                case "has_type":
                    // Backend expects: tags[has_type]=Client
                    tagParams[`tags[has_type]`] = tagTypeSlug;
                    break;
                case "has_value":
                    if (config.value) {
                        // Backend expects: tags[has_value]=Carrefour
                        tagParams[`tags[has_value]`] = config.value;
                    }
                    break;
                case "exact_values":
                    if (config.values && config.values.length > 0) {
                        // Use has_value for each value - backend will OR them
                        config.values.forEach((value) => {
                            if (!tagParams[`tags[has_value]`]) {
                                tagParams[`tags[has_value]`] = value;
                            } else {
                                // For multiple values, use type_and_value instead
                                tagParams[`tags[type_and_value]`] = JSON.stringify({
                                    type: tagTypeSlug,
                                    value: value,
                                });
                            }
                        });
                    }
                    break;
                case "type_and_value":
                    if (config.value) {
                        // Backend expects: tags[type_and_value] with JSON
                        tagParams[`tags[type_and_value]`] = JSON.stringify({
                            type: tagTypeSlug,
                            value: config.value,
                        });
                    }
                    break;
                case "predefined":
                    tagParams[`tags[predefined]`] = true;
                    break;
            }
        });

        return tagParams;
    };

    const tagApiParams = transformTagFiltersToApiParams(tagFilters);

    const sortingCacheKeys = JSON.stringify(sortingParams);
    const filtersCacheKeys = JSON.stringify(filters);
    const tagFiltersCacheKeys = JSON.stringify(tagFilters);
    const query = useQuery({
        queryKey: [
            productGridKey,
            page,
            itemsPerPage,
            search,
            effectiveProductLineFilter,
            campaignId,
            scoresQuery,
            sortingCacheKeys,
            filtersCacheKeys,
            tagFiltersCacheKeys,
        ],
        staleTime: 0,
        cacheTime: cacheTime,
        enabled: isEnabled,
        queryFn: async () => {
            const url = `/api/products`;

            const params: AxiosParams = {
                page,
                itemsPerPage,
                q: search !== "" ? search : undefined,
                campaign: campaignId,

                ...scoresQuery,
                ...sortingParams,
                ...filters,
                ...tagApiParams,
            };

            return await loggedInApiCall.get(url, { params });
        },
        select: (request) => {
            try {
                const totalItems = request.data["hydra:totalItems"];
                const rawProductGrid = request.data["hydra:member"]; // Don't cast to GridProduct[] yet
                const appSearch = hydraSearchToAppSearch(request.data["hydra:search"]);

                const data = zodParseWithLog(rawProductGrid, GridProductSchema.array(), "allProduct");

                return {
                    items: data,
                    totalItems: totalItems,
                    appSearch,
                };
            } catch (error) {
                return {
                    items: [],
                    totalItems: 0,
                    appSearch: {},
                };
            }
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        products: query.data?.items,
        totalItems: query.data?.totalItems,
        isReady,
        appSearch: query.data?.appSearch ?? {},
        // apiParams,
        ...query,
    };
};

export type ProductLine = {
    "@id": IRI;
    id: number;
    name: string;
    hierarchyPath?: string; // "Parent › Child › Current"
    hierarchyArray?: Array<{ id: number; name: string }>; // Full hierarchy as array
};

const ProductLineZodSchema = z
    .object({
        "@id": z.string(),
        id: z.number(),
        name: z.string(),
        hierarchyPath: z.string().optional(),
        hierarchyArray: z
            .array(
                z.object({
                    id: z.number(),
                    name: z.string(),
                }),
            )
            .optional(),
    })
    .passthrough();

export type Brand = {
    "@id": IRI;
    id: number;
    name: string;
    logoUrl?: string | null;
};

const BrandSchema = z
    .object({
        "@id": z.string(),
        id: z.number(),
        name: z.string(),
        logoUrl: z.string().nullable().optional(),
    })
    .passthrough();

type Campaign = {
    "@id": string;
};

const CampaignSchema = z
    .object({
        "@id": z.string(),
    })
    .passthrough();

export type GridProduct = {
    id: number;
    title: string;
    productLine?: ProductLine;
    clientId?: string;
    brand?: Brand;
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
    organic?: boolean;
    olapScores: OlapScoresResult;
    tags?: Array<{
        type: string;
        value: string;
        type_id?: string;
        value_id?: string;
        color?: string;
        predefined?: boolean;
    }>; // Format réel des tags
    jsonAttributes?: Record<string, any>;
};

// Schéma pour accepter le vrai format des tags depuis l'API
// Bug API Platform : renvoie [] au lieu de null/undefined quand pas de valeur
const TagsSchema = z.any().transform(
    (
        value,
    ): Array<{
        type: string;
        value: string;
        type_id?: string;
        value_id?: string;
        color?: string;
        predefined?: boolean;
    }> => {
        // Cas 1: Array vide - Bug API Platform qui renvoie [] au lieu de null
        if (Array.isArray(value) && value.length === 0) {
            return [];
        }

        // Cas 2: Array non vide - vérifier si c'est un vrai tableau de tags
        if (Array.isArray(value) && value.length > 0) {
            const firstItem = value[0];

            // Si c'est un vrai objet tag
            if (firstItem && typeof firstItem === "object" && "type" in firstItem && "value" in firstItem) {
                return value as Array<{
                    type: string;
                    value: string;
                    type_id?: string;
                    value_id?: string;
                    color?: string;
                    predefined?: boolean;
                }>;
            }
        }

        // Cas 3: Objet avec format {"client":["Carrefour"]} - API Platform transforme le format
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
            const result: Array<{
                type: string;
                value: string;
                type_id?: string;
                value_id?: string;
                color?: string;
                predefined?: boolean;
            }> = [];

            Object.entries(value).forEach(([type, values]) => {
                if (Array.isArray(values)) {
                    values.forEach((val) => {
                        if (typeof val === "string") {
                            result.push({
                                type: type,
                                value: val,
                            });
                        }
                    });
                }
            });

            return result;
        }

        // Cas 4: null, undefined, ou autres valeurs
        if (!value || value === null || value === undefined) {
            return [];
        }

        // Cas 5: chaîne vide ou objet vide
        if (value === "" || (typeof value === "object" && Object.keys(value).length === 0)) {
            return [];
        }

        // Fallback: tableau vide
        return [];
    },
) as z.ZodType<
    Array<{ type: string; value: string; type_id?: string; value_id?: string; color?: string; predefined?: boolean }>
>;

// Schéma pour accepter soit un objet soit un tableau vide (API Platform)
const JsonAttributesSchema = z
    .union([
        z.record(z.string(), z.any()), // Format objet standard
        z.array(z.never()).length(0), // Tableau vide uniquement
    ])
    .transform((value): Record<string, any> => {
        // Convertir tableau vide en objet vide pour cohérence
        return Array.isArray(value) ? {} : value;
    }) as z.ZodType<Record<string, any>>;

export const GridProductSchema = z
    .object({
        id: z.number(),
        title: z.string(),
        productLine: ProductLineZodSchema.optional(),
        clientId: z.string().optional(),
        brand: BrandSchema.optional(),
        canView: z.boolean(),
        canModify: z.boolean(),
        canDelete: z.boolean(),
        organic: z.boolean().optional(),
        olapScores: OlapScoresResultSchema,
        tags: TagsSchema.optional(), // Accepte objet ou tableau vide
        jsonAttributes: JsonAttributesSchema.optional(), // Accepte objet ou tableau vide
    })
    .passthrough();
